import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export type AuditScope =
  | 'agent'
  | 'kb'
  | 'creator'
  | 'permission'
  | 'module_link'
  | 'security';

export type AuditAction = 'create' | 'update' | 'delete';

export interface AuditEvent {
  id: number;
  occurred_at: string;
  actor_user_id: string | null;
  actor_label: string | null;
  scope: AuditScope;
  action: AuditAction;
  target_type: string;
  target_id: string | null;
  target_label: string | null;
  /** Only the fields that changed: `{ field: [old, new] }`. */
  changes: Record<string, unknown[]> | null;
}

export interface AuditPage {
  events: AuditEvent[];
  /** Rows matching the filter inside the frozen window. */
  total: number;
  /** Newest id when the paging session started — carried back on every page
   *  so new entries arriving mid-read cannot shift the offsets. */
  anchor_id: number | null;
}

export interface AuditFilter {
  q?: string | null;
  scope?: AuditScope | null;
  action?: AuditAction | null;
  days: number;
  limit?: number;
  offset?: number;
  anchor_id?: number | null;
}

@Injectable({ providedIn: 'root' })
export class AuditService {
  constructor(private http: HttpClient) {}

  list(filter: AuditFilter): Observable<AuditPage> {
    return this.http.get<AuditPage>(
      `${environment.tutorsBackend}/admin/audit`,
      { params: this.toParams(filter) }
    );
  }

  /**
   * CSV of the same filtered rows.
   *
   * Fetched as a blob rather than opened as a URL: the request needs the auth
   * header the interceptor adds, and a plain `window.open` would arrive
   * unauthenticated.
   */
  download(filter: AuditFilter): Observable<Blob> {
    return this.http.get(`${environment.tutorsBackend}/admin/audit/export`, {
      params: this.toParams({
        ...filter,
        anchor_id: null,
        offset: undefined,
        limit: undefined,
      }),
      responseType: 'blob',
    });
  }

  private toParams(filter: AuditFilter): HttpParams {
    let params = new HttpParams().set('days', String(filter.days));
    if (filter.q?.trim()) params = params.set('q', filter.q.trim());
    if (filter.scope) params = params.set('scope', filter.scope);
    if (filter.action) params = params.set('action', filter.action);
    if (filter.limit) params = params.set('limit', String(filter.limit));
    if (filter.offset) params = params.set('offset', String(filter.offset));
    if (filter.anchor_id) {
      params = params.set('anchor_id', String(filter.anchor_id));
    }
    return params;
  }
}
